import React from 'react'
import PropTypes from 'prop-types'
import './WingsHero.css'

/**
 * WingsHero Component
 *
 * Full-width hero component with a responsive background image and overlay.
 *
 * @component
 * @example
 * <WingsHero imageSrc="https://example.com/hero.jpg" imageAlt="Hero image">
 *   <WingsHeading level={1}>Welcome</WingsHeading>
 * </WingsHero>
 *
 * @example
 * // Dark overlay with left-aligned text
 * <WingsHero
 *   imageSrc="https://example.com/hero.jpg"
 *   overlayOpacity="dark"
 *   textAlign="left"
 *   alignItems="end"
 * >
 *   <WingsButton variant="primary">Get Started</WingsButton>
 * </WingsHero>
 */
const WingsHero = ({
  imageSrc: heroImgSrc,
  imageAlt = 'Hero image',
  profileImageSrc,
  profileImageAlt = 'Profile image',
  children,
  overlayOpacity = 'medium',
  textAlign = 'center',
  alignItems = 'center',
  className = '',
}) => {
  const heroClasses = [
    'wings-hero',
    `wings-hero-overlay-${overlayOpacity}`,
    `wings-hero-text-${textAlign}`,
    `wings-hero-align-${alignItems}`,
    className,
  ]
    .filter(Boolean)
    .join(' ')
  

  return (
    <section className={heroClasses}>
      {heroImgSrc && (
        <div className="wings-hero-image-wrapper">
          <picture>
            <source media="(max-width: 760px)" srcSet={`${heroImgSrc}?fit=crop&crop=entropy&w=760&h=400`} />
            <source media="(max-width: 1024px)" srcSet={`${heroImgSrc}?fit=crop&crop=entropy&w=1024&h=450`} />
            <source media="(max-width: 1440px)" srcSet={`${heroImgSrc}?fit=crop&crop=entropy&w=1440&h=500`} />
            <source media="(max-width: 1920px)" srcSet={`${heroImgSrc}?fit=crop&crop=entropy&w=1920&h=600`} />
            <source media="(min-width: 1921px)" srcSet={`${heroImgSrc}?fit=crop&crop=entropy&w=2560&h=700`} />
            <img src={heroImgSrc} alt={imageAlt} className="wings-hero-image" />
          </picture>
          {overlayOpacity !== 'none' && <div className="wings-hero-overlay" />}
        </div>
      )}

      <div className="wings-hero-content">
        {children && <div className="wings-hero-children">{children}</div>}
      </div>
    </section>
  )
}

WingsHero.propTypes = {
  /** Background image URL */
  imageSrc: PropTypes.string,
  /** Alt text for the background image */
  imageAlt: PropTypes.string,
  /** Profile image URL — renders the floating image card in the spacer bar */
  profileImageSrc: PropTypes.string,
  /** Alt text for the profile image */
  profileImageAlt: PropTypes.string,
  /** Content to render inside the hero */
  children: PropTypes.node,
  /** Overlay opacity level */
  overlayOpacity: PropTypes.oneOf(['none', 'light', 'medium', 'dark']),
  /** Horizontal text alignment */
  textAlign: PropTypes.oneOf(['left', 'center', 'right']),
  /** Vertical content alignment */
  alignItems: PropTypes.oneOf(['start', 'center', 'end']),
  /** Additional CSS classes */
  className: PropTypes.string,
}

export default WingsHero
